import {
  OreFormGroup,
  OreHeading,
  OreRadioButton,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'
import { useEffect, useState } from 'react'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { createApiContractRepository } from '@/Contract/infrastructure'
import { useContractDataController } from '@/Contract/ui/hooks'
import { ContractBillingDataFormAddress } from '@/Contract/ui/molecules'
import { handleErrors } from '@/Shared/ui/form'
import { navigationRoutes } from '@/Shared/ui/routers'
import { createApiStoreRepository } from '@/Store/infrastructure'
import { StoreStickersForm, useStoreController } from '@/Store/ui/hooks'
import { MyStoreAddressFormRestaurant } from '@/Store/ui/molecules/MyStoreAddressFormRestaurant'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { useUserData } from '@/User/ui/context'
import { useWebTagPermissions } from '@/User/context'
import { WebTagPermissionEnum } from '@/Shared/domain'

enum AddressViewOptions {
  StoreAddress = 1,
  BillingAddress = 2,
}

interface StoreStickersFormAddressProps {
  form: UseFormReturn<StoreStickersForm>
  canGetMaterials: boolean
  isNursery?: boolean
}

export const StoreStickersFormAddress = ({
  form: { register, formState, reset, getValues },
  canGetMaterials,
  isNursery,
}: StoreStickersFormAddressProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = useWebTagPermissions()
  const [addressView, setAddressView] = useState<AddressViewOptions>(
    AddressViewOptions.StoreAddress,
  )

  const [adressesReady, setAdressesReady] = useState<boolean>(false)

  const { onChange, ...rest } = register('addressId')
  const { errors } = handleErrors(formState)

  const { merchant } = useUserData()

  const { form: storeFormAddress } = useStoreController(
    createApiStoreRepository(),
    merchant,
    true,
  )

  const { form: billingFormAddress } = useContractDataController(
    createApiContractRepository(),
    merchant.id,
  )

  useEffect(() => {
    if (storeFormAddress.getValues('addressId')) {
      reset({
        ...getValues(),
        addressId: storeFormAddress.getValues('addressId').toString(),
      })
    }
  }, [storeFormAddress.getValues('addressId')])

  useEffect(() => {
    if (
      storeFormAddress.getValues('addressId') &&
      storeFormAddress.getValues('addressId') !== 0 &&
      billingFormAddress.getValues('addressId') &&
      billingFormAddress.getValues('addressId') !== 0
    ) {
      setAdressesReady(true)
    }
  }, [storeFormAddress.getValues('addressId'), billingFormAddress.getValues('addressId')])

  const editLink = (): JSX.Element => {
    const url =
      addressView === AddressViewOptions.StoreAddress
        ? navigationRoutes.storeData
        : navigationRoutes.contractsBillingData

    const copyKey =
      addressView === AddressViewOptions.StoreAddress
        ? isNursery ? 'addressGuarderiaDisclaimerLink1' : 'addressDisclaimerLink1'
        : 'addressDisclaimerLink2'

    return <a href={url}>{t(storesTranslation.stickers.form[copyKey])}</a>
  }

  const show = hasPermission(WebTagPermissionEnum.ACTION_LOCAL_DATA_CHANGE)
  const showBilling = hasPermission(WebTagPermissionEnum.ACTION_BILLING_DATA_CHANGE);

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(storesTranslation.stickers.form.title2)}
      </OreHeading>
      {adressesReady ? (
        <>
          <OreFormGroup direction="row" {...errors('addressId')}>
            <OreRadioButton
              {...rest}
              label={merchant.isNursery ? t(storesTranslation.stickers.form.addressChoice1.guarderiaLabel) 
                : t(storesTranslation.stickers.form.addressChoice1.label)}
              value={storeFormAddress.getValues('addressId')}
              onChange={e => {
                setAddressView(AddressViewOptions.StoreAddress)
                onChange(e)
              }}
              checked={addressView === AddressViewOptions.StoreAddress}
              disabled={!canGetMaterials}
            />
            <OreRadioButton
              {...rest}
              data-testid="billingAddress"
              label={t(storesTranslation.stickers.form.addressChoice2.label)}
              value={billingFormAddress.getValues('addressId')}
              onChange={e => {
                setAddressView(AddressViewOptions.BillingAddress)
                onChange(e)
              }}
              checked={addressView === AddressViewOptions.BillingAddress}
              disabled={!canGetMaterials}
            />
          </OreFormGroup>
          <OreText>
            {`${t(storesTranslation.stickers.form.addressDisclaimer)} `}
            {editLink()}
          </OreText>
          {addressView === AddressViewOptions.StoreAddress ? (
            <FormProvider {...storeFormAddress}>
              <MyStoreAddressFormRestaurant readOnly noMap show={show} merchant={merchant}/>
            </FormProvider>
          ) : (
            <FormProvider {...billingFormAddress}>
              <ContractBillingDataFormAddress readOnly show={showBilling} />
            </FormProvider>
          )}
        </>
      ) : (
        merchant.isNursery ? (
          <OreText>
            {t(storesTranslation.stickers.form.noAddressesGuarderia)}
            <a 
              onClick={(e) => {
                e.preventDefault()
                navigate(navigationRoutes.storeData)
              }}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              {t(storesTranslation.stickers.form.noAddressesGuarderia1)}
            </a>
            {t(storesTranslation.stickers.form.noAddressesGuarderia2)}
            <a 
              onClick={(e) => {
                e.preventDefault()
                navigate(navigationRoutes.contractsBillingData)
              }}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              {t(storesTranslation.stickers.form.noAddressesGuarderia3)}
            </a>
          </OreText>
        ) : 
        (<OreText>{t(storesTranslation.stickers.form.noAddresses)}</OreText>)
        
      )}
    </OreStack>
  )
}
